import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { geoNaturalEarth1, geoPath, geoCentroid } from 'd3-geo';
import { feature, mesh } from 'topojson-client';

// World topojson (names + ISO numeric ids). Self-hosted (world-atlas) so the map has
// no external dependency and can't vanish if a CDN goes down. Two tiers: 110m is the
// default every map runs on, but it drops every country too small to draw at that
// scale — Singapore, Hong Kong, Malta and 70 others simply aren't in it, so a country
// highlighted there silently never paints. The detailed set carries them at ~10x the
// geometry to path (and the forum index mounts ten maps a page), so it is opt-in per
// mount rather than the default.
const TOPO_COARSE = '/data/geo/countries-110m.json';
export const TOPO_DETAILED = '/data/geo/countries-50m.json';
// Per-country sub-national (state/province) geometry, keyed on ISO 3166-2. Self-hosted
// and loaded on demand — only for countries that have a state-level chapter on the page.
const STATE_URL = (cc) => `/data/geo/states/${cc}.json`;
const W = 960;
const H = 500;
// The world projection leaves whitespace top/bottom; the SVG viewBox crops to this
// window. Shared by both modes so every map has the same height/aspect (0.44).
const VIEWBOX = { x: 50, y: 48, w: 910, h: 400 };
// Focus zoom: margin kept around the chapter's countries, as a fraction of the crop.
// Higher = more breathing room around the region = less zoom.
const FOCUS_PAD = 0.16;

const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z]/g, '');
const iso3 = (id) => String(id).padStart(3, '0');
// ISO 3166-2 -> the country slice that holds it ("US-NY" -> "us").
const stateCountry = (code) => String(code).split('-')[0].toLowerCase();

// Shared across every map instance so N cards on one page fetch + parse each file
// once (the forum index renders one focus map per chapter). Cleared on failure so a
// later mount can retry.
const topoCache = {};
const loadTopo = (url) => {
    if (!topoCache[url]) {
        topoCache[url] = fetch(url)
            .then((r) => r.json())
            .catch((e) => {
                delete topoCache[url];
                throw e;
            });
    }
    return topoCache[url];
};

// `focus` zooms/centers the projection on the highlighted chapters' regions
// (chapter pages) instead of the fixed world view (events page).
// `namesOnHover` drops the pin/label layer and names regions through the tooltip
// instead — for maps with too many highlighted regions for labels to sit side by side.
// `pinOnlyStates`: a state's geometry places its pin but never fills the state's own
// shape — the country fills instead, fully, for every country any bucket belongs to
// (state-tagged or not). The default (false) is the chapter behavior: a state's fill
// replaces its country's, e.g. a chapter that covers only Texas doesn't paint all of
// the US. See engagementMapConfig()'s docblock for why the Speaking map wants the
// other way around.
const WorldMap = ({
    chapters = [],
    activeChapters = [],
    onToggleChapter,
    focus = false,
    topoUrl = TOPO_COARSE,
    namesOnHover = false,
    pinOnlyStates = false,
}) => {
    const [topo, setTopo] = useState(null);
    const [stateTopos, setStateTopos] = useState({}); // { us: topojson, ... }
    const [tip, setTip] = useState(null); // { x, y, label }
    const [tipPos, setTipPos] = useState(null); // measured { top, left }, see effect below
    const tipRef = useRef(null);
    const [hovered, setHovered] = useState(null); // chapter slug under the cursor
    const wrapRef = useRef(null);
    const [labelSize, setLabelSize] = useState(14); // SVG units, counter-scaled below

    // Logo URLs seen finish loading, so the tooltip's logo slot can shimmer until then
    // instead of popping in blank. Keyed by URL rather than reset per hover: once an
    // image has loaded this session it won't shimmer again on a repeat hover.
    const [loadedLogos, setLoadedLogos] = useState(() => new Set());
    const markLogoLoaded = (url) => setLoadedLogos((prev) => (prev.has(url) ? prev : new Set(prev).add(url)));

    // The engagement tooltip holds real links, but it's a separate element from the
    // pin/region that opened it — the cursor crosses empty space between the two, and
    // a plain mouseleave would close it mid-crossing. Closing on a short delay instead
    // (cancelled if the cursor lands on the pin/region/tooltip again meanwhile) gives it
    // time to actually arrive, the same bridge tooltip libraries use for hoverable content.
    const hideTimer = useRef(null);
    const cancelHide = () => {
        if (hideTimer.current) {
            clearTimeout(hideTimer.current);
            hideTimer.current = null;
        }
    };
    const scheduleHide = () => {
        cancelHide();
        hideTimer.current = setTimeout(() => {
            setHovered(null);
            setTip(null);
        }, 150);
    };
    useEffect(() => () => cancelHide(), []);

    // A single-line tooltip always clears the map; an engagement map's list (several
    // rows, each with a logo) can grow tall enough near the top of the hero to reach
    // the page's sticky nav — which sits above it regardless of z-index, since
    // .hero-inner opens its own stacking context the tooltip can't escape. Clamping the
    // tooltip inside the map's own box sidesteps that entirely: it never gets near the
    // nav to begin with. Runs after layout (not the usual effect) so the reposition
    // lands before paint — no visible jump to a wrong spot first.
    useLayoutEffect(() => {
        if (!tip || !tipRef.current || !wrapRef.current) {
            setTipPos(null);
            return;
        }
        const { width, height } = tipRef.current.getBoundingClientRect();
        const wrap = wrapRef.current.getBoundingClientRect();
        const margin = 8;

        let top = tip.y - height - margin; // above the cursor by default
        if (top < wrap.top + margin) top = tip.y + margin; // no room above: flip below

        let left = tip.x - width / 2; // centered under the cursor by default
        left = Math.min(Math.max(left, wrap.left + margin), wrap.right - width - margin);
        top = Math.min(Math.max(top, wrap.top + margin), wrap.bottom - height - margin);

        setTipPos({ top, left });
    }, [tip]);

    useEffect(() => {
        let alive = true;
        loadTopo(topoUrl)
            .then((d) => alive && setTopo(d))
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, [topoUrl]);

    // Which per-country state files are needed, derived from the chapters' stateIds.
    const stateSources = useMemo(() => {
        const set = new Set();
        chapters.forEach((ch) => (ch.stateIds || []).forEach((code) => set.add(stateCountry(code))));
        return [...set];
    }, [chapters]);

    // Fetch each needed state file (shared cache dedups across instances).
    useEffect(() => {
        let alive = true;
        stateSources.forEach((cc) => {
            if (!cc) return;
            loadTopo(STATE_URL(cc))
                .then((d) => alive && setStateTopos((prev) => (prev[cc] ? prev : { ...prev, [cc]: d })))
                .catch(() => {});
        });
        return () => {
            alive = false;
        };
    }, [stateSources]);

    const geo = useMemo(() => {
        if (!topo) return null;
        const fc = feature(topo, topo.objects.countries);

        // Natural Earth folds the French overseas departments into France's geometry
        // under one ISO id, so highlighting France also paints Guiana, the Caribbean and
        // (at 50m) Mayotte and Réunion — and skews its centroid / focus zoom. Keep only
        // the rings that fall inside metropolitan France + Corsica. Same pattern can be
        // extended if other countries show overseas territories.
        fc.features.forEach((f) => {
            if (normalize(f.properties?.name) === 'france' && f.geometry?.type === 'MultiPolygon') {
                f.geometry.coordinates = f.geometry.coordinates.filter(([ring]) =>
                    ring.every(([lon, lat]) => lon > -10 && lon < 10 && lat > 40 && lat < 52)
                );
            }
        });

        const borders = mesh(topo, topo.objects.countries, (a, b) => a !== b);

        // ISO 3166-1 numeric id -> chapter, for pin attribution — needs exactly one
        // owner, so a bucket that also carries stateIds is excluded here even if it
        // lists a country too (pinOnlyStates): its pin stays scoped to just its own
        // state's geometry below, never the whole country's.
        const idToChapter = {};
        chapters.forEach((ch) => {
            if ((ch.stateIds || []).length) return;
            (ch.countryIds || []).forEach((id) => {
                idToChapter[iso3(id)] = ch;
            });
        });

        // Who a country's own shape opens on hover/click — idToChapter everywhere
        // except pinOnlyStates. There, every bucket in a country now shares one
        // combined tooltip (engagementMapConfig() groups events by country, not by
        // state), so the country's shape should open it too even when every one of
        // its buckets is state-specific — any bucket is as good as any other since
        // they all carry the same events/flagUrl/moreCount already.
        const regionChapter = pinOnlyStates ? {} : idToChapter;
        if (pinOnlyStates) {
            chapters.forEach((ch) => {
                (ch.countryIds || []).forEach((id) => {
                    const key = iso3(id);
                    if (!regionChapter[key]) regionChapter[key] = ch;
                });
            });
        }

        // pinOnlyStates only: every country any bucket belongs to, state-specific or
        // not — unlike idToChapter above, multiple buckets sharing the same country id
        // is expected here (California and Utah both list "US"), since this only
        // decides whether the country's own shape paints, never who owns its pin.
        const filledCountryIds = new Set(
            pinOnlyStates ? chapters.flatMap((ch) => (ch.countryIds || []).map(iso3)) : []
        );

        // ISO 3166-2 code -> chapter (state-level chapters), plus the state geometry
        // that has loaded so far. States overlay the country base; a country belongs to
        // at most one chapter, and so does each state.
        const codeToChapter = {};
        chapters.forEach((ch) => {
            (ch.stateIds || []).forEach((code) => {
                codeToChapter[code] = ch;
            });
        });
        const stateFeatures = [];
        const stateBorders = [];
        Object.values(stateTopos).forEach((st) => {
            stateFeatures.push(...feature(st, st.objects.states).features);
            stateBorders.push(mesh(st, st.objects.states, (a, b) => a !== b));
        });

        // Countries whose states are drawn: hide their base polygon so two translucent
        // fills don't stack (which tints the whole country darker than the rest) —
        // pinOnlyStates wants the opposite (the country stays filled, states never
        // paint their own shape at all — see the render below), so nothing to cover.
        const coveredCountryIds = pinOnlyStates
            ? new Set()
            : new Set(Object.values(stateTopos).map((st) => st.country).filter(Boolean).map(iso3));

        // Focus mode fits the projection to the highlighted regions (countries and/or
        // states); otherwise the fixed world view used by the events page.
        const projection = geoNaturalEarth1();
        const focusFeatures = focus
            ? [
                  ...fc.features.filter((f) => idToChapter[iso3(f.id)]),
                  ...stateFeatures.filter((f) => codeToChapter[f.id]),
              ]
            : [];
        if (focusFeatures.length) {
            const px = VIEWBOX.w * FOCUS_PAD;
            const py = VIEWBOX.h * FOCUS_PAD;
            projection.fitExtent(
                [[VIEWBOX.x + px, VIEWBOX.y + py], [VIEWBOX.x + VIEWBOX.w - px, VIEWBOX.y + VIEWBOX.h - py]],
                { type: 'FeatureCollection', features: focusFeatures }
            );
        } else {
            projection.scale(162).translate([W / 2, H / 2 + 18]);
        }
        const path = geoPath(projection);

        // One pin per chapter at the (geographic) centroid of its features (countries
        // and/or states).
        const grouped = {};
        const addFeature = (ch, f) => {
            // flagUrl/events/moreCount are the engagement-map's tooltip fields (see
            // engagementMapConfig()); a plain chapter carries none of them, so they
            // fall through as undefined and the tooltip just shows the name, as before.
            if (!grouped[ch.slug]) {
                grouped[ch.slug] = {
                    slug: ch.slug,
                    name: ch.name,
                    flagUrl: ch.flagUrl,
                    events: ch.events,
                    moreCount: ch.moreCount,
                    features: [],
                };
            }
            grouped[ch.slug].features.push(f);
        };
        fc.features.forEach((f) => {
            const ch = idToChapter[iso3(f.id)];
            if (ch) addFeature(ch, f);
        });
        stateFeatures.forEach((f) => {
            const ch = codeToChapter[f.id];
            if (ch) addFeature(ch, f);
        });
        const pins = Object.values(grouped)
            .map((c) => {
                const [lon, lat] = geoCentroid({ type: 'FeatureCollection', features: c.features });
                const [x, y] = projection([lon, lat]) || [];
                return Number.isFinite(x) && Number.isFinite(y)
                    ? { slug: c.slug, name: c.name, flagUrl: c.flagUrl, events: c.events, moreCount: c.moreCount, x, y }
                    : null;
            })
            .filter(Boolean);

        return { features: fc.features, borders, path, idToChapter, regionChapter, pins, stateFeatures, stateBorders, codeToChapter, coveredCountryIds, filledCountryIds };
    }, [topo, chapters, focus, stateTopos, pinOnlyStates]);

    // Counter-scale the label font so it tracks the map but never renders below
    // ~12px (the SVG viewBox would otherwise shrink the text on small screens).
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const update = () => {
            const w = el.clientWidth;
            if (!w) return;
            const scale = w / VIEWBOX.w; // viewBox width
            const px = Math.max(12, Math.min(16, 14 * scale));
            setLabelSize(px / scale);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [geo]);

    if (!geo) return null;

    const active = new Set(activeChapters);
    // Clicking a region belongs to the events map alone (the one that passes a filter
    // toggle); the chapter maps are display-only. Hovering is a separate question: a
    // hover-named map has no labels, so the tooltip is the only way to read a name.
    const clickable = typeof onToggleChapter === 'function';
    const hoverable = clickable || namesOnHover;

    // Region and pin hover share one tooltip shape. flagUrl/events/moreCount are
    // undefined on a plain chapter, so the tooltip below just renders the name.
    const tipFor = (entity, x, y) => ({
        label: entity.name,
        flagUrl: entity.flagUrl,
        events: entity.events,
        moreCount: entity.moreCount,
        x,
        y,
    });

    // One region polygon (country or state). `ch` is its owning chapter (for
    // interactivity: regionChapter for the country layer, codeToChapter for states),
    // or null. `isState` gives the fainter subdivision stroke. `forceFilled`
    // (pinOnlyStates' country layer only) paints at full strength regardless of
    // whether `ch` is active-filtered — a country lit up only via its states'
    // pins is still "fully on", not partially. Interactivity still follows `ch`
    // itself, forceFilled or not: pinOnlyStates' regionChapter fills it in for
    // exactly this case, so the shape opens the same tooltip its pins do.
    const renderRegion = (f, ch, key, isState = false, forceFilled = false) => {
        const cls =
            'map-country' +
            (isState ? ' map-country--state' : '') +
            (forceFilled || ch ? ' has-chapter' : '') +
            (ch && hovered === ch.slug ? ' is-hovered' : '') +
            (forceFilled || (ch && active.has(ch.slug)) ? ' active-filter' : '');
        return (
            <path
                key={key}
                className={cls}
                d={geo.path(f)}
                onClick={ch && onToggleChapter ? () => onToggleChapter(ch.slug) : undefined}
                onMouseEnter={
                    hoverable && ch
                        ? (e) => {
                              cancelHide();
                              setHovered(ch.slug);
                              setTip(tipFor(ch, e.clientX, e.clientY));
                          }
                        : undefined
                }
                onMouseMove={hoverable && ch ? (e) => setTip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : t)) : undefined}
                onMouseLeave={hoverable && ch ? scheduleHide : undefined}
            />
        );
    };

    return (
        <div className="map-wrap" ref={wrapRef}>
            <svg className={`world-map${focus ? ' world-map--focus' : ''}${clickable ? '' : ' world-map--static'}${namesOnHover ? ' world-map--hover' : ''}`} viewBox={`${VIEWBOX.x} ${VIEWBOX.y} ${VIEWBOX.w} ${VIEWBOX.h}`} xmlns="http://www.w3.org/2000/svg" aria-label="Chapter coverage map">
                <g>
                    {geo.features.map((f, i) => {
                        const id = iso3(f.id);
                        if (geo.coveredCountryIds.has(id)) return null;
                        return renderRegion(f, geo.regionChapter[id], `c${i}`, false, pinOnlyStates && geo.filledCountryIds.has(id));
                    })}
                    <path className="map-borders" d={geo.path(geo.borders)} />

                    {/* State overlay: drawn above countries so a chapter's states paint on
                        top of the (un-highlighted) country base. pinOnlyStates skips this
                        entirely — the country above is already filled for these, and a
                        state's own shape would just double up on top of it; its geometry
                        still loaded above, only for the pin centroid below. */}
                    {!pinOnlyStates && geo.stateFeatures.map((f, i) => renderRegion(f, geo.codeToChapter[f.id], `s${i}`, true))}
                    {!pinOnlyStates && geo.stateBorders.map((b, i) => (
                        <path key={`sb${i}`} className="map-borders map-borders--state" d={geo.path(b)} />
                    ))}

                    {/* Pins first, then labels — so every label renders above every pin.
                        A hover-named map has no label to fall back on, so its pin never
                        hides and becomes its own hover target (the region beneath it can
                        be too small to aim at reliably — Singapore is a few pixels wide). */}
                    {geo.pins.map((p) => (
                        <svg
                            key={p.slug}
                            className={`map-pin__icon${!namesOnHover && hovered === p.slug ? ' is-hidden' : ''}`}
                            x={p.x - 7}
                            y={p.y - 14}
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            overflow="visible"
                            onMouseEnter={
                                namesOnHover
                                    ? (e) => {
                                          cancelHide();
                                          setHovered(p.slug);
                                          setTip(tipFor(p, e.clientX, e.clientY));
                                      }
                                    : undefined
                            }
                            onMouseMove={namesOnHover ? (e) => setTip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : t)) : undefined}
                            onMouseLeave={namesOnHover ? scheduleHide : undefined}
                        >
                            <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
                            <circle cx="12" cy="10" r="2.5" />
                        </svg>
                    ))}
                    {!namesOnHover && geo.pins.map((p) => (
                        <text
                            key={p.slug}
                            className={`map-pin__label${hovered === p.slug ? ' is-hidden' : ''}`}
                            x={p.x}
                            y={p.y - 16}
                            textAnchor="middle"
                            style={{ fontSize: labelSize }}
                        >
                            {p.name}
                        </text>
                    ))}
                </g>
            </svg>
            {tip && (
                <div
                    ref={tipRef}
                    className={`map-tooltip${tip.events ? ' map-tooltip--interactive' : ''}`}
                    // First paint has nothing measured yet (tipPos null) — render off-screen
                    // rather than flashing at the raw, unclamped cursor position for a frame.
                    style={tipPos ? { left: tipPos.left, top: tipPos.top } : { left: tip.x, top: tip.y, visibility: 'hidden' }}
                    // Only the interactive (--interactive) variant receives pointer events at
                    // all (see the CSS), so these are no-ops for the plain chapter tooltip.
                    onMouseEnter={cancelHide}
                    onMouseLeave={scheduleHide}
                >
                    {/* Plain chapter maps carry no events list, so this is the whole
                        tooltip there — same single-line name as before. */}
                    {!tip.events && <span className="map-tooltip__title">{tip.label}</span>}
                    {/* Scrolls past a handful of rows instead of cutting the list off —
                        engagementMapConfig() ships up to ENGAGEMENT_TOOLTIP_LIMIT (a payload
                        backstop, not a display cap), so this is what actually bounds the height. */}
                    {tip.events && (
                        <div className="map-tooltip__rows">
                            {tip.events.map((ev, i) => {
                                const logoLoading = ev.logoUrl && !loadedLogos.has(ev.logoUrl);
                                return (
                                    <a className="map-tooltip__row" href={ev.url || undefined} key={i}>
                                        {/* Fixed size regardless of content, so every row lines up the same —
                                            a missing logo leaves this empty rather than shrinking the row. */}
                                        <div className={`map-tooltip__logo-slot${logoLoading ? ' is-loading' : ''}`}>
                                            {ev.logoUrl && (
                                                <img
                                                    className="map-tooltip__logo"
                                                    src={ev.logoUrl}
                                                    alt=""
                                                    onLoad={() => markLogoLoaded(ev.logoUrl)}
                                                    onError={() => markLogoLoaded(ev.logoUrl)}
                                                />
                                            )}
                                        </div>
                                        <div className="map-tooltip__row-body">
                                            <div className="map-tooltip__row-head">
                                                {tip.flagUrl && <img className="map-tooltip__flag" src={tip.flagUrl} alt="" />}
                                                <span className="map-tooltip__country">{ev.location}</span>
                                                <span className="map-tooltip__date">{ev.date}</span>
                                            </div>
                                            <div className="map-tooltip__name">{ev.name}</div>
                                        </div>
                                    </a>
                                );
                            })}
                        </div>
                    )}
                    {/* Only past ENGAGEMENT_TOOLTIP_LIMIT itself now — an ordinary long list
                        just scrolls instead. */}
                    {tip.moreCount > 0 && <span className="map-tooltip__more">+{tip.moreCount} more</span>}
                </div>
            )}
        </div>
    );
};

export default WorldMap;
